// English catalog — the source of truth. Every other language mirrors this shape.
// Missing keys in another language fall back to the value here, so a partial
// translation degrades gracefully to English rather than showing a blank.
//
// Safety note: the `safety` block carries crisis-routing content, which is
// COUNTRY-specific, not just language-specific. English ships the US resources
// (988 / 911). Every other language must have these verified by a person for the
// countries it serves — see es.ts for the responsible generic pattern.

const en = {
  common: {
    seeDemo: 'See Demo',
    logIn: 'Log In',
    startFree: 'Start Free',
    back: 'Back',
    contactUs: 'Contact us',
    getSchoolLicense: 'Get a school license',
    language: 'Language',
  },

  languagePicker: {
    label: 'Language',
    availableNow: 'Available now',
    comingSoon: 'Coming soon',
  },

  landing: {
    heroBadge: 'Free 7-Day Trial · No Credit Card',
    heroTitle: 'Finally understand why your students do what they do.',
    heroSubtitle: 'Decode My Student gives every K-12 teacher instant access to brain science, behavioral strategies, and personal support — for every student struggle, every single day.',
    startTrial: 'Start Free 7-Day Trial',
    pathPrompt: 'Start free — pick what fits you:',
    teacherPath: "👩‍🏫 I'm a teacher",
    parentPath: "👪 I'm a parent",
    seeDemo: 'See a live demo — no login needed',
    skipTrial: 'Skip the trial — subscribe now ($29/mo or $299/yr)',
    noCard: 'No credit card required for the trial · Full access from day one',

    credibilityQuote: '"Built by the founder of We R H.O.P.E., the first school-embedded brain health coaching program in the United States. Informed by neuroscience and trauma-informed practice."',
    badgeNeuro: 'Neuroscience-Based',
    badgeTrauma: 'Trauma-Informed',

    problemsTitle: 'Sound familiar?',
    problems: [
      { t: 'You have a student you cannot figure out.', d: 'Nothing you try is working. You have tried everything and you are running out of ideas.' },
      { t: 'You know something is wrong.', d: "But you don't know what to do or who to call. You are doing this alone." },
      { t: 'You are exhausted.', d: 'And nobody is asking how YOU are doing. You matter too.' },
    ],

    builtToHelp: 'Here is what we built to help.',
    features: [
      { title: 'Voice or text input', desc: 'Describe any struggle in your own words' },
      { title: 'Plain-language brain science', desc: 'Understand the why behind every behavior' },
      { title: 'Real strategies', desc: 'Specific, actionable, ready to use today' },
      { title: 'Student profiles', desc: 'Gets smarter about each student over time' },
      { title: 'Teacher support', desc: 'Your wellbeing matters here too' },
      { title: 'Privacy built in', desc: 'Initials only, never full names' },
    ],

    pricingTitle: 'Simple, honest pricing',
    bestValue: 'BEST VALUE',
    annualPlan: 'Annual Plan',
    annualPrice: '$24.92',
    perMonth: '/mo',
    annualBilled: 'Billed as $299/year · Save $49',
    startFreeTrial: 'Start Free Trial',
    subscribeAnnually: 'Subscribe Annually →',
    monthlyPlan: 'Monthly Plan',
    monthlyPrice: '$29',
    monthlyBilled: 'Cancel anytime · No contracts',
    subscribeMonthly: 'Subscribe Monthly →',
    trialNoRenew: 'Your free trial does not renew automatically — no charge unless you choose a plan.',
    schoolPlan: 'School or district plan?',

    faqTitle: 'Questions teachers ask',
    faqs: [
      { q: 'Is student data safe?', a: "Decode My Student never stores full student names, ID numbers, or any personally identifiable information. Teachers use initials and grade level only. All data is encrypted in your browser and never sold or shared with third parties. The app is designed to support FERPA-conscious use." },
      { q: 'Do I need admin approval?', a: "No. At $29 per month most teachers sign up on their own and expense it later. There is nothing to install on school devices — it runs in any browser. School and district licenses are also available." },
      { q: 'Can I expense this through my school?', a: "Yes. Many teachers expense Decode My Student as a professional development tool. We can provide an invoice in whatever format your school requires." },
    ],

    closingTitle: 'Your students need you to understand them.',
    closingSubtitle: 'Start free today. No credit card. Full access for 7 days.',
    closingCta: 'Start Free — No Credit Card',
    closingFootnote: 'Built for teachers. Grounded in brain science.',
    replayEntrance: 'Replay the classroom entrance',
  },

  safety: {
    heading: 'This needs a person, not an app.',
    intro: "What you described may involve a student's safety. Decode My Student won't weigh in on this — please reach the right people now.",
    schoolLabel: 'Your school comes first',
    schoolBody: "Follow your school's crisis protocol and contact your school counselor, administrator, or crisis/safety team now.",
    // Crisis line — US specifics. Other languages MUST localize this per country.
    lifelineLabel: '988 Suicide & Crisis Lifeline',
    lifelineBody: 'Call or text {tel} to reach trained counselors, 24/7.',
    lifelineTel: '988',
    dangerLabel: 'Immediate danger',
    dangerBody: 'If someone is in immediate danger, call {tel}.',
    dangerTel: '911',
    reporterLabel: 'If you suspect abuse or neglect',
    reporterBody: 'You are a mandated reporter. If you suspect abuse or neglect, you are required to report it per your state and district policy.',
    // Shown only for non-US locales: a universal directory of crisis lines.
    internationalLabel: '',
    internationalBody: '',
  },

  disclaimer: {
    text: 'Decode My Student is a professional development tool. It does not diagnose, treat, or provide clinical advice about any child.',
    terms: 'Terms',
  },

  demoPreview: {
    title: "See what you'll get",
    subtitle: "Describe a situation in your own words. Here's the kind of support you'll see back.",
    exampleBanner: 'EXAMPLE — not a real student or a live AI result',
    grade: 'Grade 5',
    quote: '"Shuts down whenever independent writing begins."',
    drivingLabel: 'WHAT MAY BE DRIVING THE BEHAVIOR',
    driving: "A blank page can read as a threat to a nervous system that's already unsure of success. The shutdown is often protection, not defiance — going still to avoid a task that feels impossible to start.",
    tryLabel: 'THREE THINGS TO TRY TODAY',
    tryToday: [
      'Offer a choice of where to start: one sentence out loud before writing it.',
      'Break the blank page into a 3-line frame so starting feels smaller.',
      'Give a quiet 2-minute "warm-up" with no grading pressure.',
    ],
    wordsLabel: 'SUGGESTED WORDS TO USE',
    words: '"You don\'t have to get it right — just get it started. Say your first sentence out loud and I\'ll write it with you."',
    escalateLabel: 'WHEN TO ESCALATE',
    escalate: "If the shutdowns spread to most of the day, come with signs of hopelessness, or don't ease with support over a few weeks, loop in your counselor or student-support team.",
    footnote: 'Your real results are specific to what you describe and stay private to you.',
  },

  footer: {
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    contact: 'Contact Us',
    rights: '© {year} Brain Health and Wellness Coach Institute. All rights reserved.',
  },
};

export default en;
export type Catalog = typeof en;
